import { FC } from 'react';
import { UserInfo } from '../../../common/model/dto/user-info';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getHormonePattern } from '../../../services/admin/admin-hormone-pattern.service';
import OvHormonePatternScansChart from './OvHormonePatternScansChart';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import { Alert, AlertTitle } from '@mui/material';

interface OvUserHormonePatternProps {
  selectedUser?: UserInfo;
}

const NUMBER_OF_SCANS = 40;

const OvUserHormonePattern: FC<OvUserHormonePatternProps> = ({
  selectedUser,
}) => {
  const { t } = useTranslation();

  const {
    data: hormonePattern,
    isPending,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['admin-user-hormone-pattern', selectedUser?.id, NUMBER_OF_SCANS],
    queryFn: () => getHormonePattern(selectedUser?.id ?? '', NUMBER_OF_SCANS),
    retry: 3,
    enabled: !!selectedUser?.id,
  });

  const isLoading = isPending || isFetching;

  return (
    <Container>
      {!isLoading && !isError && (
        <>
          {hormonePattern?.scans?.length ? (
            <InfoCard>
              <OvHormonePatternScansChart scans={hormonePattern.scans} />
            </InfoCard>
          ) : null}

          <InfoCard>
            {hormonePattern?.name ? (
              <>
                {hormonePattern?.name && (
                  <NameHeading>{hormonePattern.name}</NameHeading>
                )}

                {hormonePattern?.short_description && (
                  <ShortDescription>
                    {hormonePattern.short_description}
                  </ShortDescription>
                )}

                {hormonePattern?.meaning && (
                  <Section>
                    <SectionTitle>
                      {t('hormonePattern.fields.meaning')}
                    </SectionTitle>
                    <RichTextBody
                      dangerouslySetInnerHTML={{
                        __html: hormonePattern.meaning,
                      }}
                    />
                  </Section>
                )}

                {hormonePattern?.lifestyle_improvement_options?.length &&
                  hormonePattern.lifestyle_improvement_options.length > 0 && (
                    <Section>
                      <SectionTitle>
                        {t('hormonePattern.fields.lifeStyleImprovements')}
                      </SectionTitle>
                      <List>
                        {hormonePattern?.lifestyle_improvement_options?.map(
                          (item, idx) => (
                            <ListItem key={`life-${idx}`}>{item}</ListItem>
                          )
                        )}
                      </List>
                    </Section>
                  )}

                {hormonePattern?.common_symptoms?.length &&
                  hormonePattern.common_symptoms.length > 0 && (
                    <Section>
                      <SectionTitle>
                        {t('hormonePattern.fields.commonSymptoms')}
                      </SectionTitle>
                      <List>
                        {hormonePattern?.common_symptoms?.map((item, index) => (
                          <ListItem key={`symp-${index}`}>{item}</ListItem>
                        ))}
                      </List>
                    </Section>
                  )}

                {hormonePattern?.questions_to_ask_your_doctor?.length &&
                  hormonePattern.questions_to_ask_your_doctor.length > 0 && (
                    <Section>
                      <SectionTitle>
                        {t('hormonePattern.fields.questionsToAskYourDoctor')}
                      </SectionTitle>
                      <List>
                        {hormonePattern?.questions_to_ask_your_doctor?.map(
                          (item, index) => (
                            <ListItem key={`q-${index}`}>{item}</ListItem>
                          )
                        )}
                      </List>
                    </Section>
                  )}
              </>
            ) : (
              <StyledAlert severity="info">
                <AlertTitle>{t('hormonePattern.alert.title')}</AlertTitle>
                {t('hormonePattern.alert.description', {
                  numberOfScanDays: hormonePattern?.total_scan_days,
                })}
              </StyledAlert>
            )}
          </InfoCard>
        </>
      )}

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </Container>
  );
};

export default OvUserHormonePattern;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const InfoCard = styled.div`
  margin-top: 1rem;
  background: ${Colours.WHITE};
  box-shadow: ${Variables.boxShadow.defaultBox};
  border-radius: ${Variables.borderRadius.LARGE};
  padding: 1rem 1.25rem;
`;

const NameHeading = styled.h3`
  margin: 0 0 0.25rem 0;
  color: ${Colours.OV_BASE};
  font-size: 1.25rem;
  font-weight: 700;
`;

const ShortDescription = styled.p`
  margin: 0 0 1rem 0;
  color: ${Colours.TEXT_DARK['500']};
  font-size: 0.95rem;
`;

const Section = styled.div`
  margin-top: 1rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.25rem 0;
  color: ${Colours.OV_BASE};
  font-weight: 600;
`;

const List = styled.ul`
  margin: 0.25rem 0 0 1rem;
  padding: 0;
  color: ${Colours.OV_BASE};
`;

const ListItem = styled.li`
  margin: 0.125rem 0;
  line-height: 1.25;
  font-size: 0.875rem;
`;

const RichTextBody = styled.div`
  margin: 0.25rem 0 0 0;
  color: ${Colours.OV_BASE};
  font-size: 0.875rem;
  line-height: 1.25;
`;

const StyledAlert = styled(Alert)`
  && {
    margin: 0.25rem 0 0.5rem 0;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_BORDER_COLOR_V3};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
